import React, { createContext, ReactNode, useContext, useMemo } from 'react'

import useDealer from 'hooks/useDealer'
import { useFeaturesContext } from 'hooks/FeaturesContext'

type Dealer = {
  id?: number
  channel?: string
  criticalAlertCount: number
  majorAlertCount: number
  minorAlertCount: number
  sdaCriticalAlertCount: number
  sdaMajorAlertCount: number
  sdaMinorAlertCount: number
  connectedCount: number
  disconnectedCount: number
  dirtyFilterCount: number
  activeServiceContractCount: number
  expiredServiceContractCount: number
  nonServiceContractCount: number
  hehmSDACriticalAlarmCount: number
  hehmSDAMajorAlarmCount: number
  hehmSDAMinorAlarmCount: number
  hehmHealthScoreCriticalCount: number
  hehmHealthScoreFairCount: number
  hehmHealthScoreGreatCount: number
  hehmFilterHealthScoreNeedsReplacementCount: number
  hehmFilterHealthScoreFairCount: number
  hehmRecentlyDisconnectedCount: number
  hehmDisconnected24HoursCount: number
  hehmDisconnected7DaysCount: number
}

type DashboardData = {
  dealer: Dealer
  isDealerLoaded: boolean
  enabledFeatures: string[]
  isEnabledFeaturesLoaded: boolean
}

const DashboardDataContext = createContext<DashboardData | null>(null)

export const DashboardDataProvider = ({ children }: { children: ReactNode }) => {
  const { dealer, isDealerLoaded } = useDealer(0)
  const { enabledFeatures, isEnabledFeaturesLoaded } = useFeaturesContext()

  const value = useMemo<DashboardData>(() => ({
    dealer: dealer as unknown as Dealer,
    isDealerLoaded,
    enabledFeatures: enabledFeatures || [],
    isEnabledFeaturesLoaded: isEnabledFeaturesLoaded || false
  }), [dealer, isDealerLoaded, enabledFeatures, isEnabledFeaturesLoaded])

  return (
    <DashboardDataContext.Provider value={value}>
      {children}
    </DashboardDataContext.Provider>
  )
}

export const useDashboardData = (): DashboardData => {
  const value = useContext(DashboardDataContext)

  if (!value) throw new Error('useDashboardData must be used within DashboardDataProvider')

  return value
}
